import { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateStoreCommissionRates } from "apis/paymentAPIs";
import { updateSelectedRestaurant } from "store/actions/restaurant";

import { noWheelInputProps } from "util/utils";
import {
  COMMISSION_FIELDS,
  DEFAULT_EFTPOS_CENTS,
  EFTPOS_MODES,
  parseCommissionForm,
  toFormValues,
} from "./commissionRates";

// Commission the merchant pays on each in-person card payment, via their own Adyen split profile.
const EditRatesDialog = ({ open, onClose, restaurant, showSuccessMessage, showErrorMessage }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(toFormValues(null));
  const [enableStandalone, setEnableStandalone] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const settings = restaurant ? restaurant.adyenPaymentSettings : null;
  const isStandalone = Boolean(settings && settings.isStandaloneTerminal && settings.isPOSSplitConfigApplied);

  useEffect(() => {
    if (open) {
      setValues(toFormValues(settings ? settings.commissionRates : null));
      setEnableStandalone(false);
    }
  }, [open, settings]);

  const saveSettings = (adyenPaymentSettings) =>
    dispatch(updateSelectedRestaurant({
      ...restaurant,
      adyenPaymentSettings: { ...settings, ...adyenPaymentSettings },
    }));

  const handleChange = (key) => (event) => setValues({ ...values, [key]: event.target.value });

  const handleEftposModeChange = (event) => {
    const eftposMode = event.target.value;
    setValues({ ...values, eftposMode, eftposValue: eftposMode === "fixed" ? DEFAULT_EFTPOS_CENTS : "" });
  };

  const eftposMode = EFTPOS_MODES.find((mode) => mode.value === values.eftposMode);

  const handleEditRatesConfirm = async () => {
    const { rates, error } = parseCommissionForm(values);
    if (error) {
      showErrorMessage(error);
      return;
    }
    try {
      setButtonLoading(true);
      const data = await updateStoreCommissionRates(restaurant.id, rates, { enableStandalone });
      saveSettings(data.adyenPaymentSettings);
      showSuccessMessage(data.applied
        ? "Commission rates applied"
        : "Commission rates saved - they apply after Adyen removes surcharges at midnight 1 Oct");
      onClose();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.error;
      // The rates can be applied even when Adyen refuses standalone mode.
      if (err?.response?.data?.applied) {
        saveSettings(err.response.data.adyenPaymentSettings);
      }
      showErrorMessage(detail ? `Failed to update rates: ${detail}` : "Failed to update rates");
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="edit-rates-dialog-title">
      <DialogTitle id="edit-rates-dialog-title">Edit Commission Rates</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Charged to the merchant on each in-person card payment. International Amex cards use the
          international rate.
        </DialogContentText>
        {COMMISSION_FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            margin="dense"
            id={key}
            label={label}
            type="number"
            inputProps={noWheelInputProps}
            value={values[key]}
            onChange={handleChange(key)}
            fullWidth
          />
        ))}
        <TextField
          select
          margin="dense"
          id="eftposMode"
          label="EFTPOS charge"
          value={values.eftposMode}
          onChange={handleEftposModeChange}
          helperText={
            values.eftposMode === "domestic" ? "No EFTPOS rule: EFTPOS payments are charged the domestic rate." : " "
          }
          fullWidth
        >
          {EFTPOS_MODES.map((mode) => (
            <MenuItem key={mode.value} value={mode.value}>
              {mode.label}
            </MenuItem>
          ))}
        </TextField>
        {eftposMode && eftposMode.fieldLabel && (
          <TextField
            margin="dense"
            id="eftposValue"
            label={eftposMode.fieldLabel}
            type="number"
            inputProps={noWheelInputProps}
            value={values.eftposValue}
            onChange={handleChange("eftposValue")}
            fullWidth
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={isStandalone || enableStandalone}
              disabled={isStandalone}
              onChange={(event) => setEnableStandalone(event.target.checked)}
            />
          }
          label={isStandalone ? "Standalone mode is enabled" : "Enable standalone mode"}
        />
        {!isStandalone && (
          <FormHelperText>
            Switches this store&apos;s Adyen terminals to standalone mode once the rates apply, which restarts
            the Adyen app on them. The POS then shows one OB Payments button instead of the card buttons.
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleEditRatesConfirm} disabled={buttonLoading} color="primary">
          Update Rates
          {buttonLoading && <CircularProgress size={30} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditRatesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  restaurant: PropTypes.shape({
    id: PropTypes.string,
    adyenPaymentSettings: PropTypes.object,
  }),
  showSuccessMessage: PropTypes.func.isRequired,
  showErrorMessage: PropTypes.func.isRequired,
};

export default EditRatesDialog;
